<template>
    <!--
        Modal presentation (per-org "terms_consent_presentation" = modal):
        read-only scroll box + "View Full Terms of Consent" modal + an explicit
        "I accept these Terms of Consent" checkbox + single "Ok" acknowledgment button.
        The button is disabled until the checkbox is checked. Enforcement/persistence are
        unchanged; this only changes how the consent is presented.
    -->
    <div v-if="modal" class="flex flex-col gap-3 w-full">
        <p v-if="reconsentNotice" class="body-small text-valence-grey-750 text-left">{{ reconsentNotice }}</p>
        <div class="max-h-60 overflow-y-auto text-left rounded-lg px-4 py-3 bg-[#f5f5f5] ring-2 ring-inset ring-[#e8e8e8]">
            <MarkdownText class="details text-valence-grey-750" :text="termsContent" />
        </div>
        <button type="button" class="self-start text-valence-primary-500 hover:underline body-small-semibold cursor-pointer" @click="isFullTermsOpen = true">
            View Full Terms of Consent
        </button>
        <BaseCheckbox v-model="termsModel" shape="square" class="body-small text-valence-grey-750 justify-center">I accept these Terms of Consent</BaseCheckbox>
        <BaseButton theme="primary" class="w-full py-4 rounded-xl font-semibold" :disabled="!termsModel" @click="acknowledge">Ok</BaseButton>

        <BaseDialog v-model:open="isFullTermsOpen" title="Terms of Use" :show-close="true" :float-close="true">
            <div class="p-6 max-w-2xl text-left">
                <h3 class="title-subsection mb-4">Terms of Use</h3>
                <MarkdownText class="details text-valence-grey-750" :text="termsContent" />
            </div>
        </BaseDialog>
    </div>

    <!--
        Default presentation: inline scrollable terms followed by an explicit
        "I accept these Terms of Consent" checkbox. Checking it records consent
        (`terms` becomes true), which is what parent pages gate their submit button on.
    -->
    <div v-else class="flex flex-col gap-3 w-full">
        <p v-if="reconsentNotice" class="body-small text-valence-grey-750 text-left">{{ reconsentNotice }}</p>
        <div class="leading-none max-h-80 overflow-y-auto text-left rounded-lg px-3 py-4 sm:px-2 sm:py-3 bg-[#f5f5f5] ring-2 ring-inset ring-[#e8e8e8]">
            <MarkdownText class="text-balance *:details *:text-valence-grey-750" :text="termsContent"></MarkdownText>
        </div>
        <BaseCheckbox v-model="termsModel" shape="square" class="body-small text-valence-grey-750 justify-center">I accept these Terms of Consent</BaseCheckbox>
    </div>
</template>

<script setup>
import BaseButton from "~vue/components/BaseButton.vue";
import BaseDialog from "~vue/components/BaseDialog.vue";
import BaseCheckbox from "~vue/components/form/BaseCheckbox.vue";
import MarkdownText from "~vue/MarkdownText.vue";
import { computed, ref } from "vue";

const props = defineProps({
    termsContent: { type: String, default: null },
    // When true, render the scroll-box + modal + "Ok" button presentation instead of the
    // default inline terms. Driven by the org's terms_consent_presentation config.
    modal: { type: Boolean, default: false },
    // Why the user is being (re-)prompted (server-computed). A non-first-time reason renders
    // a one-line notice above the terms; full-page interstitials say it in their own heading
    // instead and must not pass this prop, to avoid double messaging.
    reconsentReason: { type: String, default: "first_time" },
});

const RECONSENT_NOTICES = {
    updated: "The terms have been updated since you last accepted them.",
    grandfathered: "We've improved our records around terms acceptance — please re-accept.",
};

const reconsentNotice = computed(() => RECONSENT_NOTICES[props.reconsentReason] ?? null);

const emit = defineEmits(["accept"]);

const termsModel = defineModel("terms", { type: Boolean });

const isFullTermsOpen = ref(false);

function acknowledge() {
    termsModel.value = true;
    emit("accept");
}
</script>

<style scoped></style>
